import * as React from "react";
import { api } from "@/lib/api";
import { fmtCents } from "@/lib/time";
import { Calendar as CalendarIcon, User2, Briefcase, MapPin } from "lucide-react";

/**
 * AsOfRecordPanel — dense "as of" viewer for a person's effective-dated
 * record.  Operator picks a date → panel reconstructs job title, rate,
 * status, home site, etc. for that day.
 *
 * Renders as a compact 2-column grid.  Values are the raw
 * hr_attribute_history rows; the parent (AttributeHistoryPanel) shows
 * the full timeline.
 */
const EMPTY = <span className="text-brand-faint">—</span>;

export default function AsOfRecordPanel({ companyId, userId }) {
  const [asOf, setAsOf] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [record, setRecord] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/companies/${companyId}/hr/members/${userId}/record`,
          { params: { as_of: asOf } },
        );
        setRecord(data);
      } catch (_) {
        setRecord(null);
      }
    })();
  }, [companyId, userId, asOf]);

  if (!record) {
    return <div className="p-3 text-[12px] text-brand-muted">Loading…</div>;
  }

  const emp = record.employment || {};

  return (
    <div data-testid="asof-record-panel">
      <div className="flex items-center gap-2 mb-2">
        <CalendarIcon size={13} className="text-brand-violet" />
        <span className="micro-label">AS OF</span>
        <input
          type="date"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          className="field-input !h-7 !w-[130px] text-[12px] mono-num"
          data-testid="asof-picker"
        />
      </div>
      <div className="card-panel p-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
        <Cell icon={<Briefcase size={11} />} label="Job title" value={emp.job_title} />
        <Cell icon={<User2 size={11} />} label="Rate"
              value={emp.rate != null ? fmtCents(emp.rate) : null} mono />
        <Cell label="Contract" value={emp.contract_type} />
        <Cell label="Status"
              value={emp.employment_status}
              tone={emp.employment_status === "terminated" ? "coral"
                    : emp.employment_status === "notice_served" ? "amber"
                    : emp.employment_status === "active" ? "emerald"
                    : ""} />
        <Cell label="FTE hours" value={emp.fte_hours != null ? `${emp.fte_hours}h/wk` : null} mono />
        <Cell icon={<MapPin size={11} />} label="Home site"
              value={emp.site_home ? emp.site_home.slice(0, 8) : null} mono />
        <Cell label="Continuous svc" value={emp.continuous_service_date} mono />
        <Cell label="Notice period"
              value={emp.notice_period_days != null ? `${emp.notice_period_days} days` : null} mono />
      </div>
    </div>
  );
}

function Cell({ icon, label, value, mono, tone }) {
  const toneClass = {
    coral: "text-coral",
    amber: "text-amber-800",
    emerald: "text-emerald-800",
  }[tone] || "text-brand-ink";
  return (
    <div className="flex items-start gap-2">
      {icon ? <span className="mt-0.5 text-brand-muted">{icon}</span> : null}
      <div className="min-w-0 flex-1">
        <div className="micro-label">{label}</div>
        <div className={`${mono ? "mono-num" : ""} ${toneClass} font-medium`}
             data-testid={`asof-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {value != null && value !== "" ? value : EMPTY}
        </div>
      </div>
    </div>
  );
}
